import { MutableRefObject, useEffect, useRef, useState } from 'react'
import translate from 'core/translate'
import helpers from 'core/helpers'
import { changeFilter, routes } from 'features/routes'
import moment from 'moment'
import { api } from 'core/api/ConnectionManager'
import helpersSchedule from 'core/helpers/_schedule'
import helpersBroadcasts from 'core/helpers/_broadcasts'
import { createMethods } from 'pages/schedule/schedule.state'
import { getURLSearchParamsByLocation, getURLSearchParamsString } from 'features/routes/utils'
import { changeLocation } from 'features/routes'
import {
    IAddScheduleRef,
    IEditableSchedule,
    IScheduleConflict,
} from '../../../blocks.app/addSchedule/addSchedule.local.methods'
import { ISchedule } from '../../../core/models/Schedule'
import {
    emptyScheduleDeviceDelta,
    IScheduleDeviceDelta,
    isGroupDevice,
    mergeSidebarDevices,
    toggleScheduleDevice,
} from './scheduleDeviceDelta'
import {
    applyDeviceToggle,
    applyGroupToggle,
    applySidebarRemovals,
    fetchGroupDevices,
    groupUiIds,
    ScheduleSelectable,
    SelectionChange,
} from './scheduleDeviceSelection'

interface IDeviceStats {
    online: number
    offline: number
    error: number
    online_display_off: number
    sleep: number
    updating: number
}

export type Device = {
    orientation?: number
    id: number
    name: string
    type?: string
    selected?: boolean
    digitalSignageStats?: IDeviceStats
    parentId?: number
}

export type Broadcast = { id: number; title: string; orientation?: number }

export interface IScheduleFilterItem {
    id: number | string
    name?: string
    title?: string
    type: string
}

interface IScheduleAddLocation {
    pathname: string
    search: string
    hash: string
    state: unknown
    key?: string
}

export interface IScheduleAddMethodsProps {
    selectedDate: number
    filterList: IScheduleFilterItem[]
    view?: { digitalSignage?: unknown }
    location: IScheduleAddLocation
    setTitle: (title: string[]) => void
    el: MutableRefObject<IAddScheduleRef | null>
}

interface IScheduleResponse extends Omit<ISchedule, 'repeatDays' | 'localRepeatDays' | 'broadcast'> {
    repeatDays: Array<number | moment.Moment | { startDate: string; endDate: string }>
    localRepeatDays: Array<number | moment.Moment | { startDate: string; endDate: string }>
    digitalSignageId?: Array<{ digitalSignageId: number }>
    devicesCount?: number
    broadcastId: number
    broadcast?: {
        title: string
        folderId?: number
    }
}

interface ISelectedItems {
    displays: Device[]
    broadcasts: Broadcast[]
}

interface ISelectFromFileResult {
    selected?: Device[]
    notSelected?: Device[]
}

interface IScheduleDevicesResponse {
    data?: Device[]
    total?: number
    nextPageToken?: string | null
}

interface IScheduleDevicesPage {
    list: Device[]
    nextPageToken: string | null
    total?: number
}

const parseScheduleDevices = (res: IScheduleDevicesResponse | Device[]): Device[] => {
    if (Array.isArray(res)) return res
    return Array.isArray(res?.data) ? res.data : []
}

const mapBoundDevices = (list: Device[]): Device[] =>
    list.map((device) => ({
        ...device,
        type: device.type || 'digitalSignage',
        selected: true,
    }))

/** Страница устройств, уже привязанных к расписанию (правый сайдбар). */
const fetchScheduleDevicesPage = async (
    pageToken?: string | null,
    scheduleId?: number,
    search?: string
): Promise<IScheduleDevicesPage> => {
    const trimmedSearch = search?.trim()
    const params = pageToken
        ? { nextPageToken: pageToken }
        : {
              scheduleId: Number(scheduleId),
              sort: 'name',
              limit: 50,
              ...(trimmedSearch ? { search: trimmedSearch } : {}),
          }

    const res = await api.send<
        { scheduleId?: number; sort?: string; limit?: number; nextPageToken?: string; search?: string },
        IScheduleDevicesResponse
    >('getScheduleDevices', params)

    return {
        list: parseScheduleDevices(res),
        nextPageToken: res.nextPageToken || null,
        total: typeof res.total === 'number' ? res.total : undefined,
    }
}

const ScheduleAddMethods = (p_: IScheduleAddMethodsProps) => {
    const [orientationWarning, setOrientationWarning] = useState<boolean>(false)
    const [saved, setSaved] = useState(false)
    const [devices, setDevices] = useState<Device[]>([])
    const [boundDevices, setBoundDevices] = useState<Device[]>([])
    const [deviceDelta, setDeviceDelta] = useState<IScheduleDeviceDelta>(emptyScheduleDeviceDelta())
    const [devicesCount, setDevicesCount] = useState(0)
    const [devicesNextPageToken, setDevicesNextPageToken] = useState<string | null>(null)
    const [devicesPageLoading, setDevicesPageLoading] = useState(false)
    const [devicesSearch, setDevicesSearch] = useState('')
    const devicesSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const scheduleIdRef = useRef<number | null>(null)
    const groupDevicesRef = useRef<Record<number, ScheduleSelectable[]>>({})
    const groupContributionRef = useRef<Record<number, number>>({})
    const deviceDeltaRef = useRef(deviceDelta)
    deviceDeltaRef.current = deviceDelta
    const [groupUiDeviceIds, setGroupUiDeviceIds] = useState<number[]>([])
    const [notSelectedDevices, setNotSelectedDevices] = useState<Device[]>([])
    const [conflicts] = useState<IScheduleConflict[]>([])
    const [contentDownloadDate, setContentDownloadDate] = useState<moment.MomentInput | null>(null)
    const [contentDownloadTime, setContentDownloadTime] = useState<string | null>(null)
    const [selectFromFileMode, setSelectFromFileMode] = useState<boolean>(false)
    const [broadcast, setBroadcast] = useState<Broadcast | null>(null)
    const [prevDevices, setPrevDevices] = useState<Device[]>([])
    const [schedule, setSchedule] = useState<IEditableSchedule>({
        startDate: moment(p_.selectedDate),
        endDate: null,
        startTime: helpersSchedule.startOf(),
        endTime: helpersSchedule.endOf(),
        repeatMode: 'forever',
        repeatDays: [],
        contentDownloadDateTime: null,
        priority: 0,
        localStartTime: helpersSchedule.startOf(),
        localEndTime: helpersSchedule.endOf(),
        localStartDate: moment(p_.selectedDate),
        localEndDate: null,
        localRepeatDays: [],
        timeToUse: 'local',
        removeOverlappingSchedules: true,
    })

    useEffect(() => {
        const pathname = helpers.getPathname(p_.location.pathname)
        const query = getURLSearchParamsByLocation(p_.location)

        if (pathname === routes.addSchedule.path) {
            p_.setTitle([translate('scheduleCreate')])

            if (p_.filterList) {
                const selectedItems = getSelectedItems(p_.filterList)
                const devices = [...selectedItems.displays]
                let broadcast = null

                const selectedBroadcast = selectedItems.broadcasts[0]

                if (
                    selectedItems.broadcasts.length === 1 &&
                    (!('instanceType' in selectedBroadcast) ||
                        !helpersBroadcasts.isFolder(selectedBroadcast as Broadcast & { instanceType: string }))
                ) {
                    broadcast = selectedBroadcast
                }

                if (devices.length > 100) {
                    setSelectFromFileMode(true)
                }

                setDevices(devices)
                setDevicesCount(devices.length)
                setDeviceDelta({
                    ...emptyScheduleDeviceDelta(),
                    addDigitalSignageIds: devices
                        .filter((device) => device.type !== 'group')
                        .map((device) => device.id),
                    addGroupIds: devices.filter((device) => device.type === 'group').map((device) => device.id),
                })
                setBroadcast(broadcast)
            }
        } else if (query.scheduleId) {
            p_.setTitle([translate('scheduleEdit')])
            getSchedule(query.scheduleId)
        }
    }, [p_.view?.digitalSignage])

    useEffect(() => {
        if (saved) {
            showCalendarAfterSave()
        }
    }, [saved])

    const setDevicesByAutoSelection = (devicesList: Device[]) => {
        setPrevDevices(devices)
        setDevices(devicesList)
        setDevicesCount(devicesList.filter((device) => device.type !== 'group').length)
    }

    const onCancelConfirm = () => {
        setDevices(prevDevices)
    }

    const getSelectedItems = (filterList: IScheduleFilterItem[] = []): ISelectedItems => {
        const listByType: { [index: string]: string } = helpersSchedule.getListByType()
        const selectedItems: ISelectedItems = {
            displays: [],
            broadcasts: [],
        }
        const typedCreateMethods = createMethods as Record<
            string,
            (id: number | string, name: string) => Device | Broadcast
        >

        filterList.forEach((filterItem) => {
            const itemType = filterItem.type
            const listType = listByType[itemType] as keyof ISelectedItems | undefined
            const createItem = typedCreateMethods[itemType]

            if (!listType || !createItem) return

            const item = createItem(filterItem.id, filterItem.name || filterItem.title || '')

            if (listType === 'displays') {
                selectedItems.displays.push(item as Device)
            } else {
                selectedItems.broadcasts.push(item as Broadcast)
            }
        })

        return selectedItems
    }

    const orientationCheckWarning = (devices: Device[], broadcast: Broadcast | null): boolean => {
        return devices.some((device) => {
            return (
                broadcast &&
                ((device.orientation === 0 && broadcast.orientation === 90) ||
                    (device.orientation === 90 && broadcast.orientation === 0) ||
                    (device.orientation === 180 && broadcast.orientation === 90) ||
                    (device.orientation === 270 && broadcast.orientation === 0))
            )
        })
    }

    const save = (cb?: () => void, warningConfirm?: boolean) => {
        setPrevDevices(devices)

        if (orientationCheckWarning(devices, broadcast) && !warningConfirm) {
            return setOrientationWarning(true)
        } else {
            setOrientationWarning(false)
            p_.el.current?.save((isValid: boolean) => {
                if (isValid) {
                    setSaved(true)
                }
                if (cb) {
                    cb()
                }
            })
        }
    }

    /** После save — на календарь с тем же фильтром, с которым заходили. */
    const showCalendarAfterSave = () => {
        changeLocation({
            pathname: `/${routes.schedule.path}`,
            search: getURLSearchParamsString({
                scheduleData: JSON.stringify(getScheduleDataFromFilterList(p_.filterList)),
            }),
        })
    }

    const getScheduleDataFromFilterList = (filterList: IScheduleFilterItem[]) => {
        const scheduleData: { [index: string]: { id: number; name: string }[] } = helpersSchedule.getInitScheduleData()

        filterList.forEach((filterItem) => {
            if (!scheduleData[filterItem.type] || typeof filterItem.id !== 'number') return

            scheduleData[filterItem.type].push({
                id: filterItem.id,
                name: filterItem.name || filterItem.title || '',
            })
        })

        for (let type in scheduleData) {
            if (scheduleData.hasOwnProperty(type)) {
                if (scheduleData[type].length === 0) {
                    delete scheduleData[type]
                }
            }
        }

        return scheduleData
    }

    /** Отмена редактирования — назад на календарь с исходным фильтром. */
    const cancel = () => {
        changeLocation({
            pathname: `/${routes.schedule.path}`,
            search: getURLSearchParamsString({
                scheduleData: JSON.stringify(getScheduleDataFromFilterList(p_.filterList)),
            }),
        })
    }
    /** Загрузить расписание для редактирования + первую страницу устройств. */
    const getSchedule = (id: string) => {
        api.send<{ id: string }, IScheduleResponse>('getSchedule', {
            id,
        }).then((data) => {
            const dates =
                data.timeToUse === 'local'
                    ? helpersSchedule.getLocalStartEndDate(data as ISchedule)
                    : helpersSchedule.getUTCStartEndDate(data as ISchedule)
            let startDate: moment.Moment | null = moment(dates.startDate)
            let endDate: moment.Moment | null = moment(dates.endDate)

            if (data.timeToUse === 'utc') {
                startDate = startDate.isValid() ? moment(startDate).local() : null
                endDate = endDate.isValid() ? moment(endDate).local() : null
            }

            if (!endDate || !endDate.isValid()) {
                endDate = null
            }

            if (!startDate || !startDate.isValid()) {
                return
            }

            let contentDownloadDateTime: string | moment.Moment | null = schedule.contentDownloadDateTime || null
            let contentDownloadDate: moment.Moment | null = null
            let contentDownloadTime: string | null = null

            if (data.repeatMode === 'weekly') {
                if (data.timeToUse !== 'local') {
                    const start = moment.utc(`${data.startDate} ${data.startTime}`, 'YYYY-MM-DD HH:mm:ss')
                    data.repeatDays = helpersSchedule.revertFormatRepeatDays(
                        data.repeatDays as number[],
                        start.toString()
                    )
                } else {
                    data.repeatDays = data.localRepeatDays
                }
            }
            if (data.repeatMode === 'exact_dates') {
                data.repeatDays = data.repeatDays.map((day) => {
                    if (moment.isMoment(day)) return day

                    return moment(typeof day === 'number' ? day : day.endDate)
                })
            }
            if (data.contentDownloadDateTime) {
                const date = moment.utc(data.contentDownloadDateTime).local()
                contentDownloadDateTime = date.format('YYYY-MM-DD HH:mm:ss')
                contentDownloadDate = date
                contentDownloadTime = date.format('HH:mm:ss')
            }

            const formattedData = {
                contentDownloadTime,
                contentDownloadDate,
                schedule: {
                    id: data.id,
                    startDate,
                    endDate,
                    startTime: startDate.format('HH:mm:ss'),
                    endTime: data.localEndTime ? data.localEndTime : helpersSchedule.endOf(),
                    localStartDate: startDate,
                    localEndDate: data.localEndDate,
                    localStartTime: startDate.format('HH:mm:ss'),
                    localEndTime: data.localEndTime,
                    repeatMode: data.repeatMode,
                    repeatDays: data.repeatDays,
                    contentDownloadDateTime,
                    localRepeatDays: data.repeatDays,
                    localContentDownloadDateTime: contentDownloadDateTime,
                    timeToUse: data.timeToUse || 'local',
                    priority: data.priority,
                    removeOverlappingSchedules: true,
                },
            }

            setContentDownloadTime(formattedData.contentDownloadTime)
            setContentDownloadDate(formattedData.contentDownloadDate)
            setSchedule(formattedData.schedule)

            const broadcastTitle = data.broadcast ? data.broadcast.title : ''

            setBroadcast({ id: data.broadcastId, title: broadcastTitle })
            setDevicesCount(typeof data.devicesCount === 'number' ? data.devicesCount : 0)
            setDeviceDelta(emptyScheduleDeviceDelta())
            deviceDeltaRef.current = emptyScheduleDeviceDelta()
            setDevices([])
            setBoundDevices([])
            setDevicesNextPageToken(null)
            setDevicesSearch('')
            setGroupUiDeviceIds([])
            groupDevicesRef.current = {}
            groupContributionRef.current = {}
            scheduleIdRef.current = Number(id)
            setTimeout(() => {
                if (data.broadcast && data.broadcast.folderId) {
                    changeFilter({ folderId: data.broadcast.folderId })
                }
            }, 200)
            setDevicesPageLoading(true)
            fetchScheduleDevicesPage(null, Number(id))
                .then((page) => {
                    if (typeof data.devicesCount !== 'number' || data.devicesCount === 0) {
                        if (page.total && page.total > 0) {
                            setDevicesCount(page.total)
                        }
                    }
                    setBoundDevices(mapBoundDevices(page.list))
                    setDevicesNextPageToken(page.nextPageToken)
                })
                .finally(() => {
                    setDevicesPageLoading(false)
                })
        })
    }

    /** Перезагрузить устройства сайдбара с поиском. */
    const reloadScheduleDevices = (searchValue: string) => {
        if (!scheduleIdRef.current) return

        setDevicesPageLoading(true)
        setBoundDevices([])
        setDevicesNextPageToken(null)
        fetchScheduleDevicesPage(null, scheduleIdRef.current, searchValue)
            .then((page) => {
                setBoundDevices(mapBoundDevices(page.list))
                setDevicesNextPageToken(page.nextPageToken)
            })
            .finally(() => {
                setDevicesPageLoading(false)
            })
    }

    const onDevicesSearchChange = (value: string) => {
        setDevicesSearch(value)
        if (devicesSearchTimeoutRef.current) {
            clearTimeout(devicesSearchTimeoutRef.current)
        }
        devicesSearchTimeoutRef.current = setTimeout(() => {
            reloadScheduleDevices(value)
        }, 400)
    }

    /** Подгрузить ещё страницу устройств расписания (скролл сайдбара). */
    const loadMoreScheduleDevices = () => {
        if (!devicesNextPageToken || devicesPageLoading) return

        setDevicesPageLoading(true)
        fetchScheduleDevicesPage(devicesNextPageToken)
            .then((page) => {
                setBoundDevices((current) => {
                    const existingIds = new Set(current.map((device) => device.id))
                    const next = mapBoundDevices(page.list).filter((device) => !existingIds.has(device.id))
                    return [...current, ...next]
                })
                setDevicesNextPageToken(page.nextPageToken)
            })
            .finally(() => {
                setDevicesPageLoading(false)
            })
    }

    const boundIds = () => new Set(boundDevices.map((device) => device.id))

    /** Сдвинуть счётчик устройств. */
    const bumpCount = (diff: number) => {
        if (!diff) return
        setDevicesCount((count) => Math.max(0, count + diff))
    }

    /** Обновить галочки устройств внутри выбранных групп. */
    const syncGroupUi = (groupIds: number[]) => {
        setGroupUiDeviceIds(groupUiIds(groupIds, groupDevicesRef.current))
    }

    /** Добавить / убрать устройства в оверлее правого сайдбара. */
    const patchSidebar = (add?: ScheduleSelectable[], removeIds?: number[]) => {
        setDevices((current) => {
            let next = current
            if (removeIds?.length) {
                const remove = new Set(removeIds)
                next = next.filter((device) => !remove.has(device.id))
            }
            if (add?.length) {
                const existing = new Set(next.map((device) => device.id))
                next = [
                    ...next,
                    ...add
                        .filter((device) => !existing.has(device.id))
                        .map((device) => ({
                            id: device.id,
                            name: device.name || '',
                            type: device.type,
                        })),
                ]
            }
            return next
        })
    }

    /** Применить результат выбора: delta, счётчик, UI групп, сайдбар. */
    const commitDeltaChange = (change: SelectionChange, options?: { groupId?: number; contribution?: number }) => {
        deviceDeltaRef.current = change.delta
        setDeviceDelta(change.delta)

        if (options?.groupId != null) {
            if (options.contribution) {
                groupContributionRef.current[options.groupId] = options.contribution
            } else {
                delete groupContributionRef.current[options.groupId]
            }
        }

        bumpCount(change.countDiff)
        syncGroupUi(change.delta.addGroupIds)
        patchSidebar(change.sidebarAdd, change.sidebarRemoveIds)
    }

    /** Клик по чекбоксу в каталоге (устройство или группа). */
    const onToggleCatalogItem = (data: { selected: boolean; item: Device & { selected?: boolean; name?: string } }) => {
        if (isGroupDevice(data.item)) {
            const groupId = data.item.id
            const run = (groupDevices: ScheduleSelectable[]) => {
                const current = deviceDeltaRef.current
                const groupWasSelected =
                    current.addGroupIds.includes(groupId) ||
                    (Boolean(data.item.selected) && !current.removeGroupIds.includes(groupId))

                const change = applyGroupToggle(
                    current,
                    groupId,
                    data.selected,
                    groupWasSelected,
                    groupDevices,
                    boundIds(),
                    groupContributionRef.current[groupId] || 0
                )

                commitDeltaChange(change, { groupId, contribution: change.contribution })
            }

            if (data.selected || groupDevicesRef.current[groupId]) {
                fetchGroupDevices(groupId, scheduleIdRef.current, groupDevicesRef.current).then(run)
                return
            }

            run([])
            return
        }

        const change = applyDeviceToggle(
            deviceDeltaRef.current,
            data.item,
            data.selected,
            boundIds(),
            groupDevicesRef.current
        )

        if (!data.selected) {
            const parentGroupId = Object.keys(groupContributionRef.current)
                .map(Number)
                .find((id) => groupDevicesRef.current[id]?.some((device) => device.id === data.item.id))
            if (parentGroupId != null && groupContributionRef.current[parentGroupId] > 0) {
                groupContributionRef.current[parentGroupId] -= 1
                if (groupContributionRef.current[parentGroupId] <= 0) {
                    delete groupContributionRef.current[parentGroupId]
                }
            }
        }

        commitDeltaChange(change)
    }

    /** Удаление из правого списка устройств. */
    const onSidebarDevicesChange = (nextDevices: Device[]) => {
        const nextIds = new Set(nextDevices.map((device) => device.id))
        const change = applySidebarRemovals(
            deviceDeltaRef.current,
            mergeSidebarDevices(boundDevices, devices, deviceDeltaRef.current),
            nextIds,
            boundIds(),
            groupDevicesRef.current
        )

        deviceDeltaRef.current = change.delta
        setDeviceDelta(change.delta)
        bumpCount(change.countDiff)
        syncGroupUi(change.delta.addGroupIds)
        setDevices((current) => current.filter((device) => nextIds.has(device.id)))
    }

    /** Снять устройства по id (например из модалки). */
    const onRemoveDevicesByIds = (ids: number[]) => {
        let next = deviceDeltaRef.current
        ids.forEach((id) => {
            next = toggleScheduleDevice(next, { id, type: 'digitalSignage', selected: true }, false).delta
        })
        deviceDeltaRef.current = next
        setDeviceDelta(next)
        bumpCount(-ids.length)
        setDevices((current) => current.filter((device) => !ids.includes(device.id)))
        setBoundDevices((current) => current.filter((device) => !ids.includes(device.id)))
    }

    /** Выбор устройств из файла. */
    const onSelectFromFile = (data: ISelectFromFileResult) => {
        setSelectFromFileMode(true)

        if (data.selected) {
            const delta = {
                ...emptyScheduleDeviceDelta(),
                addDigitalSignageIds: data.selected
                    .filter((device) => device.type !== 'group')
                    .map((device) => device.id),
                addGroupIds: data.selected.filter((device) => device.type === 'group').map((device) => device.id),
            }
            setDevices(data.selected)
            setDevicesCount(data.selected.filter((device) => device.type !== 'group').length)
            deviceDeltaRef.current = delta
            setDeviceDelta(delta)
            setGroupUiDeviceIds([])
            groupContributionRef.current = {}
        }
        if (data.notSelected) {
            setNotSelectedDevices(data.notSelected)
        }
    }

    /** Сбросить выбор из файла. */
    const onClearSelectFromFile = () => {
        setDevices([])
        setNotSelectedDevices([])
        setSelectFromFileMode(false)
        deviceDeltaRef.current = emptyScheduleDeviceDelta()
        setDeviceDelta(emptyScheduleDeviceDelta())
        setGroupUiDeviceIds([])
        groupContributionRef.current = {}
        setDevicesCount(boundDevices.length)
    }

    return {
        devices: mergeSidebarDevices(boundDevices, devices, deviceDelta) as Device[],
        setDevices,
        deviceDelta,
        groupUiDeviceIds,
        devicesCount,
        onToggleCatalogItem,
        onSidebarDevicesChange,
        onRemoveDevicesByIds,
        conflicts,
        save,
        cancel,
        broadcast,
        setBroadcast,
        advDuration: 0,
        advContentLength: 0,
        schedule,
        setSchedule,
        showCalendarAfterSave,
        contentDownloadDate,
        contentDownloadTime,
        setContentDownloadDate,
        setContentDownloadTime,
        orientationWarning,
        setOrientationWarning,
        onSelectFromFile,
        selectFromFileMode,
        onClearSelectFromFile,
        notSelectedDevices,
        setDevicesByAutoSelection,
        onCancelConfirm,
        loadMoreScheduleDevices,
        devicesNextPageToken,
        devicesPageLoading,
        devicesSearch,
        onDevicesSearchChange,
    }
}

export default ScheduleAddMethods
